import dayjs from "dayjs";

/** Shape of react-i18next's t() we rely on (avoids version-coupled TFunction generics). */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Deadline urgency scale for the Suivi dashboard (design contract):
 *   grey ("calm")  — far away              — neutral dot + text
 *   amber ("warn") — within warnDays       — amber dot + text
 *   red ("hot")    — today/tomorrow-ish    — soft red dot + text
 *   "past"         — window closed         — grey, non-actionable
 * Chips are text+dot, never filled backgrounds; red exists ONLY at "hot".
 *
 * Thresholds are a CONFIG, not literals — Outborn's real window durations will
 * decide them (weeks-long windows make everything grey, day-long ones make
 * everything red; tune here, not in the components).
 */
export const URGENCY_THRESHOLDS = {
  /** days-left <= hotDays  → "hot" (red) */
  hotDays: 1,
  /** days-left <= warnDays → "warn" (amber) */
  warnDays: 3,
};

export type Urgency = "calm" | "warn" | "hot" | "past";

/**
 * Whole-day difference, both sides floored to local start-of-day — deadlines are
 * end-of-day APP_TZ (Europe/Paris) timestamps; comparing raw instants around
 * midnight would be off by one for users near the timezone boundary.
 */
export const getDaysLeft = (endDate: string | Date): number =>
  dayjs(endDate).startOf("day").diff(dayjs().startOf("day"), "day");

export const getUrgency = (endDate: string | Date): Urgency => {
  const daysLeft = getDaysLeft(endDate);
  if (daysLeft < 0) return "past";
  if (daysLeft <= URGENCY_THRESHOLDS.hotDays) return "hot";
  if (daysLeft <= URGENCY_THRESHOLDS.warnDays) return "warn";
  return "calm";
};

/** Chip text: « Aujourd'hui » / « Demain » / « J-N » / « Expiré ». */
export const formatDeadline = (t: Translate, endDate: string | Date): string => {
  const daysLeft = getDaysLeft(endDate);
  if (daysLeft < 0) return t("suivi_due_past");
  if (daysLeft === 0) return t("suivi_due_today");
  if (daysLeft === 1) return t("suivi_due_tomorrow");
  return t("suivi_due_in_days", { count: daysLeft });
};

/** Dot/text colors per urgency (calm inherits the muted ink). `halo` is a soft
 *  ring around the dot — presence for warn/hot without a filled chip. */
export const URGENCY_COLORS: Record<Urgency, { dot: string; text: string; halo: string }> = {
  calm: {
    dot: "#94a3b8",
    text: "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6)",
    halo: "transparent",
  },
  warn: { dot: "#f59e0b", text: "#b45309", halo: "rgba(245, 158, 11, 0.2)" },
  hot: { dot: "#ef4444", text: "#dc2626", halo: "rgba(239, 68, 68, 0.18)" },
  past: {
    dot: "#94a3b8",
    text: "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.5)",
    halo: "transparent",
  },
};
